/**
 * The cookie banner.
 *
 * Consent is recorded through Shopify's Customer Privacy API, which is what
 * actually gates tracking. A banner that only looked like it recorded a choice
 * would be worse than no banner at all.
 *
 * Nothing is pre-ticked and every toggle starts off. Under India's DPDP Act a
 * pre-ticked consent control is illegal, and rejecting has to be exactly as
 * easy as accepting. The floating card has two equal buttons, Accept and
 * Decline; the inline control on the cookie policy page keeps the per-purpose
 * toggles.
 *
 * The card only shows where Shopify's Customer Privacy API says consent is
 * required (shouldShowBanner), and the choice is recorded with
 * setTrackingConsent and remembered.
 */
const OPTIONAL = ['analytics', 'marketing', 'preferences'];
const CONSENT_KEY = 'stella:cookie-consent';

class CookieBanner extends HTMLElement {
  async connectedCallback() {
    this.choose = this.querySelector('[data-choose]');
    this.panel = this.querySelector('.cookie__panel');
    this.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-cookie-preferences]')) return;
      event.preventDefault();
      this.openPreferences();
    });
    /* Inline, on the cookie policy page, it is the page's own control: always
       shown, choices open, and saving confirms rather than hides. */
    this.inline = this.hasAttribute('data-inline');

    if (!this.inline) {
      try {
        if (localStorage.getItem(CONSENT_KEY)) return;
      } catch { /* Shopify remains the source of truth when storage is unavailable. */ }
    }

    const privacy = await this.privacyApi();
    /* If the API never arrives, showing a banner that cannot record anything
       would be dishonest. Stay hidden and let Shopify's own banner handle it. */
    if (!privacy) return;

    if (this.inline) {
      this.hidden = false;
      if (this.choose) this.choose.hidden = false;
      this.reflect(privacy);
      return;
    }

    const current = privacy.currentVisitorConsent?.() || {};
    const hasChoice = OPTIONAL.every((purpose) => current[purpose] === 'yes' || current[purpose] === 'no');
    if (!hasChoice && privacy.shouldShowBanner()) {
      this.hidden = false;
      this.keepClearOfStickyBar();
    }
  }

  /* On a product page the add-to-cart bar is fixed to the bottom on mobile.
     The card sits above it, never on it. */
  keepClearOfStickyBar() {
    const bar = document.querySelector('.sticky-bar');
    const update = () => {
      const visible = bar && !bar.hidden && bar.getBoundingClientRect().height > 0;
      this.style.setProperty('--cookie-offset', visible ? `${bar.getBoundingClientRect().height}px` : '0px');
    };
    update();
    if (!bar) return;
    new MutationObserver(update).observe(bar, { attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
    window.addEventListener('resize', update, { passive: true });
  }

  /* On the policy page the toggles show what is currently granted, so a
     person can see their choice before changing it. */
  reflect(privacy) {
    const current = privacy.currentVisitorConsent?.() || {};
    this.querySelectorAll('[data-purpose]').forEach((t) => {
      t.setAttribute('aria-checked', String(current[t.dataset.purpose] === 'yes'));
    });
  }

  async openPreferences() {
    const privacy = await this.privacyApi();
    if (privacy) this.reflect(privacy);
    this.reopened = true;
    this.hidden = false;
    /* The floating card has no second panel: reopening shows Accept and
       Decline again. Only the inline policy-page control has the toggles. */
    if (!this.choose) {
      if (this.panel) this.panel.hidden = false;
      this.keepClearOfStickyBar();
      return;
    }
    if (this.panel) this.panel.hidden = true;
    this.choose.hidden = false;
  }

  /* The API is injected by Shopify and may not be ready at parse time. */
  privacyApi() {
    return new Promise((resolve) => {
      if (window.Shopify?.customerPrivacy) return resolve(window.Shopify.customerPrivacy);
      document.addEventListener(
        'visitorConsentCollected',
        () => resolve(window.Shopify?.customerPrivacy),
        { once: true }
      );
      window.Shopify?.loadFeatures?.(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        () => resolve(window.Shopify?.customerPrivacy)
      );
      setTimeout(() => resolve(window.Shopify?.customerPrivacy), 3000);
    });
  }

  onClick(event) {
    const toggle = event.target.closest('[data-purpose]');
    if (toggle) {
      toggle.setAttribute('aria-checked', toggle.getAttribute('aria-checked') === 'true' ? 'false' : 'true');
      return;
    }

    const action = event.target.closest('[data-consent]')?.dataset.consent;
    if (!action) return;

    if (action === 'choose') {
      if (this.panel) this.panel.hidden = true;
      this.choose.hidden = false;
      return;
    }
    if (action === 'close') {
      this.choose.hidden = true;
      if (this.reopened) {
        this.hidden = true;
        this.reopened = false;
      } else if (this.panel) {
        this.panel.hidden = false;
      }
      return;
    }

    const granted = {};
    if (action === 'all') {
      OPTIONAL.forEach((p) => (granted[p] = true));
    } else if (action === 'essential') {
      OPTIONAL.forEach((p) => (granted[p] = false));
    } else if (action === 'save') {
      OPTIONAL.forEach((p) => (granted[p] = false));
      this.querySelectorAll('[data-purpose]').forEach((t) => {
        granted[t.dataset.purpose] = t.getAttribute('aria-checked') === 'true';
      });
    }

    this.record(granted);
  }

  record(granted) {
    try { localStorage.setItem(CONSENT_KEY, JSON.stringify(granted)); } catch { /* API still records it. */ }
    if (!this.inline) this.hidden = true;
    let completed = false;
    const complete = () => {
      if (completed) return;
      completed = true;
      if (this.inline) {
        const done = this.querySelector('[data-consent-done]');
        if (done) done.hidden = false;
        return;
      }
      this.hidden = true;
    };
    window.Shopify?.customerPrivacy?.setTrackingConsent(granted, complete);
    window.setTimeout(complete, 1200);
  }
}

customElements.define('cookie-banner', CookieBanner);
